
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { debounce } from "typescript-debounce-decorator";
import httpClient from "../../store/httpClient";
import { MahnungDto } from "@/dtos/mahnungDto";
import { MahnungAuditDto } from "@/dtos/mahnungAuditDto";
import { EventType } from "@/dtos/eventType";
import { AuditType } from "@/dtos/auditType";
import DatePicker from "../../components/DatePicker.vue";
import MahnungAddressOverview from "@/components/MahnungAddressOverview.vue";
import { MahnungPositionDto } from "@/dtos/mahnungPositionDto";
import { AddressDto } from "@/dtos/addressDto";
import rules from "@/store/rules";

@Component({
	components: {
		"date-picker": DatePicker,
		"mahnung-address-overview": MahnungAddressOverview,
	},
})
export default class OpenMahnungen extends Vue {
	@Prop()
	private reloadMahnungen!: boolean;
	private isLoading: boolean = false;
	private isDownloading: boolean = false;
	private openMahnungDialog: boolean = false;
	private currentMahnung: MahnungDto = new MahnungDto();
	private sendSelectedMahnungenDialog: boolean = false;
	private lastNote: string = "";
	private validReminderAddressForm: boolean = false;
	private addressRequiredStringRule = rules.addressRequiredStringRule;
	private addressOptionalStringRule = rules.addressOptionalStringRule;
	private optionalEmailRule = rules.optionalEmailRule;
	private newAudit: MahnungAuditDto = new MahnungAuditDto();
	readonly auditTemplate: MahnungAuditDto = {
		auditId: 0,
		createdAt: new Date(),
		mahnungId: 0,
		note: "",
		email: "",
		eventType: EventType.MahnungSentAsEmail,
	};

	private auditEventTypes: any = [
		{
			id: EventType.MahnungSentAsEmail,
			text: "Per Email versendet",
		},
		{
			id: EventType.MahnungSentAsPhysicalMail,
			text: "Per Post versendet",
		},
		{
			id: EventType.MahnungCalledCustomer,
			text: "Per Telefon kontaktiert",
		},
	];

	private headers: Array<{
		text: string;
		value: string;
		align: string;
		sortable: boolean;
		width: string;
	}> = Array(
		{
			text: "",
			value: "controls",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Mahnungs-Id",
			value: "mahnungsId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Kunden-Id",
			value: "customerId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Kundenadresse",
			value: "customerAddress.companyName",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Erstellt",
			value: "createdAt",
			align: "left",
			sortable: true,
			width: "10px",
		},
		{
			text: "Mahnungsdatum",
			value: "mahnungsDatum",
			align: "left",
			sortable: true,
			width: "10px",
		},
		{
			text: "Rechnungs-Ids",
			value: "invoiceIds",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Mahnungsgebühren",
			value: "mahnungCost",
			align: "right",
			sortable: true,
			width: "1px",
		},
		{
			text: "Offener Betrag",
			value: "openInvoiceAmount",
			align: "right",
			sortable: false,
			width: "1px",
		}
	);

	private auditHeaders: Array<{
		text: string;
		align: string;
		sortable: boolean;
		value: string;
	}> = Array(
		{
			text: "Datum",
			align: "left",
			sortable: false,
			value: "date",
		},
		{
			text: "Event",
			align: "left",
			sortable: false,
			value: "eventType",
		},
		{
			text: "Bemerkung",
			align: "left",
			sortable: false,
			value: "note",
		}
	);

	private eventTypeText: string[] = [
		"Mahnung per Email verschickt",
		"Mahnung per Post verschickt",
		"Kunde bezüglich der Mahnung angerufen",
	];

	private mounted() {
		if (this.mahnungen.length === 0) {
			this.loadMahnungen();
		}
	}

	@Watch("reloadMahnungen")
	private async reload() {
		if (this.reloadMahnungen && !this.isLoading) {
			await this.loadMahnungen();
		}
		this.$emit("resetReloadToggle");
	}

	private get mahnungen() {
		return this.$store.state.mahnungen;
	}
	private set mahnungen(newMahnungen: MahnungDto[]) {
		this.$store.commit("setMahnungen", newMahnungen);
	}

	private get searchTerm(): string {
		return this.$store.state.mahnungSearchTerm;
	}
	private set searchTerm(searchTerm: string) {
		this.$store.commit("setMahnungSearchTerm", searchTerm);
	}

	private get lastSearchTerm(): string {
		return this.$store.state.mahnungLastSearchTerm;
	}
	private set lastSearchTerm(lastSearchTerm: string) {
		this.$store.commit("setMahnungLastSearchTerm", lastSearchTerm);
	}

	private get totalOpenBills() {
		return this.mahnungen.length;
	}

	private get mahnungSorting() {
		return this.$store.state.mahnungSorting;
	}
	private set mahnungSorting(mahnungSorting: any) {
		this.$store.commit("setMahnungSorting", mahnungSorting);
	}

	private get auditSorting() {
		return this.$store.state.auditSorting;
	}

	private changeSort(column: string) {
		if (this.mahnungSorting.sortBy === column) {
			this.mahnungSorting.descending = !this.mahnungSorting.descending;
		} else {
			this.mahnungSorting.sortBy = column;
			this.mahnungSorting.descending = false;
		}
	}

	private async loadMahnungen() {
		if (this.isLoading) {
			return;
		}
		try {
			this.isLoading = true;
			console.log("Loading mahnungen...");
			let data: any = {
				stringContent: this.searchTerm,
			};
			const response = await httpClient().post("Invoice/GetAllOpenMahnungen", data);
			this.mahnungen = response.data;
		} catch (e) {
			console.log("Loading mahnungen failed.", e);
		} finally {
			this.isLoading = false;
		}
	}

	@debounce(700, { leading: false })
	@Watch("searchTerm")
	private reloadOnSearchTermChange() {
		if (
			!this.isLoading &&
			this.lastSearchTerm !== this.searchTerm &&
			(this.searchTerm.length >= 3 || this.searchTerm.length === 0)
		) {
			this.lastSearchTerm = this.searchTerm;
			this.loadMahnungen();
		}
	}

	private getOpenBalance(positions: MahnungPositionDto[]) {
		let totalMoney = 0;
		for (const pos of positions) {
			totalMoney += Math.round((pos.invoiceOpenAmount + Number.EPSILON) * 100) / 100;
		}
		return totalMoney;
	}

	private getInvoiceIds(positions: MahnungPositionDto[]) {
		return positions.map((p: MahnungPositionDto) => p.invoiceId);
	}

	private onMahnungClick(mahnungsId: number) {
		this.currentMahnung = this.mahnungen.filter(
			(m: MahnungDto) => m.mahnungsId === mahnungsId
		)[0];
		if (!this.currentMahnung.reminderAddress) {
			this.currentMahnung.reminderAddress = new AddressDto();
		}
		this.lastNote = this.currentMahnung.note;
		this.newAudit = { ...this.auditTemplate }; // get a shallow copy of the template
		this.openMahnungDialog = true;
	}

	private async getMahnungPdf() {
		if (this.isDownloading) {
			return;
		}
		try {
			this.isDownloading = true;
			console.log(`Generating Mahungs-PDF for Mahnung ${this.currentMahnung.mahnungsId}`);
			const response = await httpClient().post(
				"Invoice/DownloadMahnungPdf",
				this.currentMahnung,
				{
					responseType: "blob",
				}
			);
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute(
				"download",
				"Zahlungserinnerung " + this.currentMahnung.mahnungsId + ".pdf"
			);
			link.click();
		} catch {
			console.log("Get Mahnung PDF failed.");
		} finally {
			this.isDownloading = false;
		}
	}

	private async downloadInvoicePdfs() {
		if (this.isDownloading) {
			return;
		}
		try {
			this.isDownloading = true;
			console.log(`Generating invoices for Mahnung ${this.currentMahnung.mahnungsId}`);
			for (const position of this.currentMahnung.mahnungsPositions) {
				await this.$store.dispatch("downloadInvoiceAsPdf", position.invoiceId);
			}
		} catch {
			console.log("Failed downloading invoices.");
		} finally {
			this.isDownloading = false;
		}
	}

	private async sendSelectedMahnungenAndReload() {
		if (this.isLoading) {
			return;
		}
		await this.sendSelectedMahnungen().then(this.loadMahnungen);
	}

	private async sendSelectedMahnungen() {
		if (this.isLoading) {
			return;
		}
		try {
			if (this.mahnungen.filter((m: MahnungDto) => m.selected).length <= 0) {
				this.$store.commit("setSnackbarText", "Keine Mahnungen selektiert.");
				return;
			}
			this.isLoading = true;
			console.log("Sending Mahnungen emails...");
			let selectedMahnungen = this.mahnungen.filter((m: MahnungDto) => m.selected);
			const response = await httpClient().post(
				"Invoice/SendSelectedMahnungen",
				selectedMahnungen
			);
			this.$store.commit("setSnackbarText", "Alle Mahnungen wurden gesendet.");
		} catch (ex) {
			console.log("Error while sending Mahnungen emails", ex);
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim senden der Mahnungen. Siehe Log."
			);
		} finally {
			this.isLoading = false;
			this.sendSelectedMahnungenDialog = false;
		}
	}

	private async saveNote() {
		if (this.isLoading || this.currentMahnung.note === this.lastNote) {
			return;
		}
		if (this.currentMahnung.note == null) {
			this.currentMahnung.note = "";
		}
		try {
			this.isLoading = true;
			console.log("Saving Mahnung note...");
			let data: any = {
				stringContent: this.currentMahnung.note,
			};
			await httpClient().post(
				`Invoice/SaveNote?mahnungsId=${this.currentMahnung.mahnungsId}`,
				data
			);
			this.lastNote = this.currentMahnung.note;
			this.$store.commit("setSnackbarText", "Bemerkung wurde gespeichert.");
		} catch (ex) {
			console.log("Error while saving note", ex);
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim speichern der Bemerkung. Siehe Log."
			);
		} finally {
			this.isLoading = false;
		}
	}

	private onAddressSelected(address: AddressDto) {
		this.currentMahnung.reminderAddress.companyName = address.companyName;
		this.currentMahnung.reminderAddress.givenName = address.givenName;
		this.currentMahnung.reminderAddress.surname = address.surname;
		this.currentMahnung.reminderAddress.street = address.street;
		this.currentMahnung.reminderAddress.zip = address.zip;
		this.currentMahnung.reminderAddress.town = address.town;
		this.currentMahnung.reminderAddress.eMail = address.eMail;
		this.$store.commit(
			"setSnackbarText",
			"Adresse wurde in die Mahnungsadresse übernommen. Bitte prüfen und speichern."
		);
	}

	private async saveMahnungAddress() {
		if (this.isLoading) {
			return;
		}
		try {
			this.isLoading = true;
			console.log("Saving Mahnung address...");
			await httpClient().post(
				`Invoice/SaveMahnungAddress?mahnungsId=${this.currentMahnung.mahnungsId}`,
				this.currentMahnung.reminderAddress
			);
			this.$store.commit("setSnackbarText", "Adresse wurde gespeichert.");
		} catch (ex) {
			console.log("Error while saving Mahnung address", ex);
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim speichern der Adresse. Siehe Log."
			);
		} finally {
			this.isLoading = false;
		}
	}

	private async saveAudit() {
		if (this.isLoading) {
			return;
		}
		try {
			this.isLoading = true;
			console.log("Saving new audit...");
			this.newAudit.mahnungId = this.currentMahnung.mahnungsId;
			await httpClient().post("Invoice/SaveAudit", this.newAudit);
			let currentMahnungen = JSON.parse(JSON.stringify(this.mahnungen));
			let current = currentMahnungen.filter(
				(m: MahnungDto) => m.mahnungsId == this.newAudit.mahnungId
			)[0];
			current.audits.push(this.newAudit);
			this.mahnungen = currentMahnungen;
			this.newAudit = { ...this.auditTemplate };
			this.$store.commit("setSnackbarText", "Ereigniss wurde gespeichert.");
		} catch (ex) {
			console.log("Error while saving event", ex);
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim speichern des Ereignisses. Siehe Log."
			);
		} finally {
			this.isLoading = false;
		}
	}

	private goToCustomer(customerId: string) {
		this.$router.push({ name: "customerDetail", params: { customerId } });
	}

	private setSelected(mahnungsId: number, newValue: any) {
		this.$store.commit("setSelectedMahnung", { mahnungsId: mahnungsId, value: newValue });
	}

	private getIconColor(audits: MahnungAuditDto[], type: EventType) {
		return audits.some((a: MahnungAuditDto) => a.eventType == type) ? "green" : "grey";
	}

	private async navigateToInvoiceEdit(invoiceId: number) {
		this.$router.push({ name: "editInvoice", params: { invoiceId: invoiceId.toString() } });
	}

	private getSortedAudits(audits: MahnungAuditDto[]) {
		let copy = JSON.parse(JSON.stringify(audits));
		return copy.sort((a: MahnungAuditDto, b: MahnungAuditDto) => {
			let aDate = new Date(a.createdAt);
			let bDate = new Date(b.createdAt);
			return bDate.getTime() - aDate.getTime();
		});
	}
}
